import { database } from '../common/dexie_database.js'
import { getCookie, hasBetaAccess, offlineModeIsActive, getURLParameter } from '../common/helpers.js'
import { ajaxRequest } from '../common/ajax.js'
import { logMessage }  from '../common/logger.js'

const TEMPLATE_SHARING_ONE_ATHLETE = 1
const TEMPLATE_SHARING_ALL_COACHES_ATHLETES = 2
const TEMPLATE_SHARING_ALL_COMPANIES_ATHLETES = 3

export var storeTemplatesLocally = function (new_data, callback) {
  if (offlineModeIsActive()) {

    var data_populated_datetime_string = new Date().toISOString();
    var new_table_metadata = {table_name: 'template_training_sessions', populated_datetime: data_populated_datetime_string };

    database.transaction("rw", database.template_training_sessions, database.template_folders, database.table_metadata, function() {

      // Add the new data to the database.
      database.table_metadata.put(new_table_metadata).then(function () {
        database.template_training_sessions.bulkPut(Object.values(new_data.template_sessions)).then(function (last_key) {
          database.template_folders.bulkPut(Object.values(new_data.template_folders)).then(function (last_key) {
            if (typeof(callback) == 'function') {
              callback(true);
            }
          });
        });
      });

    });

  }
  else {
    if (typeof(callback) == 'function') {
      callback(false);
    }
  }

}

function deleteTemplateSessionsLocally(template_training_session_ids, callback) {
  database.template_training_sessions.bulkDelete(template_training_session_ids).then(callback);
}

function deleteFoldersLocally(folder_ids) {
  database.template_folders.bulkDelete(folder_ids);
}

function getValuesFromObjects(objects, key) {

  let values = [];
  if (objects.constructor === Array) {
    for (let i = 0; i < objects.length; i++) {
      values.push(objects[i][key]);
    }
  }
  else {
    Object.values(objects).forEach(object => {
      values.push(object[key]);
    });
  }

  return values;
}

function getValuesOnlyInListA(a, b) {
  var values = [];
  for (var i = 0; i < a.length; i++) {
    if ($.inArray(parseInt(a[i]), b) === -1) {
      values.push(parseInt(a[i]))
    }
  }
  return values;
}

export const fetchTemplatesFromServer = function (athlete_id, group_id, successCallback, extra_request_data) {

  if (!extra_request_data) {
    var extra_request_data = {};
  }

  const impersonate_user_id = getURLParameter('current-user-id');
  if (impersonate_user_id) {
    extra_request_data['current_user_id'] = impersonate_user_id;
  }

  if (athlete_id) {
    extra_request_data['athlete_id'] = athlete_id;
  }
  else if (group_id) {
    extra_request_data['group_id'] = group_id;
  }

  ajaxRequest({
    url: '/coach/fetch-templates/',
    method: 'GET',
    data: extra_request_data,
    success: successCallback,
    expected_response_type: 'json',
  });

}

/**
 * Load template training sessions from persistent storage.
 */
export const loadTemplateTrainingSessions = function (athlete_id, group_id, templates_loaded_callback) {

  let local_data = { template_sessions: [], template_folders: [] };

  var templatesFetchedFromServer = function (new_data, athlete_id, group_id) {
    if (xhale.user_preference.offline_mode.is_enabled()) {

      // Any templates in the current offline store that are not fetched from
      // the server should be deleted locally. It's possible a session just
      // got shared differently so no longer appears for this user but will for
      // another, but it should have been updated in any case and this is just
      // a backup way of clearing old data that somehow fell through the cracks.
      // If we did get rid of something accidentally it would also sync with
      // the server again when opening up another athlete. So there are all
      // kinds of ways that data will self correct.
      //
      // After we start storing new templates locally before syncing with the
      // server, we will have to first check that the local data is not flagged
      // as being unsaved on the server.

      let old_data_template_ids = getValuesFromObjects(local_data.template_sessions, 'id');
      let new_data_template_ids = getValuesFromObjects(new_data.template_sessions, 'id');
      let template_ids_to_delete = getValuesOnlyInListA(old_data_template_ids, new_data_template_ids);

      if (template_ids_to_delete.length > 0) {
        deleteTemplateSessionsLocally(template_ids_to_delete);
      }

      // The same with folders.
      let old_data_folder_ids = getValuesFromObjects(local_data.template_folders, 'id');
      let new_data_folder_ids = getValuesFromObjects(new_data.template_folders, 'id');
      let folder_ids_to_delete = getValuesOnlyInListA(old_data_folder_ids, new_data_folder_ids);

      if (folder_ids_to_delete.length > 0) {
        deleteFoldersLocally(folder_ids_to_delete);
      }

      storeTemplatesLocally(new_data, xhale_meta_data.user_id);

      return templates_loaded_callback(new_data, template_ids_to_delete, folder_ids_to_delete, 'server');
    }
    else {
      // There won't be anything to delete if offline mode is not enabled
      // as it is a fresh load.
      return templates_loaded_callback(new_data, [], [], 'server');
    }

  }

  if (athlete_id) {

    var extra_request_data = {}
    var skip_offline_access;

    if (xhale.helper.get_url_parameter('show_unshared')) {
      extra_request_data.show_unshared = true;
      skip_offline_access = true;
    }

    if (xhale.helper.get_url_parameter('current_user_id')) {
      extra_request_data.current_user_id = xhale.helper.get_url_parameter('current_user_id');
      skip_offline_access = true;
    }

    // Attempt to load the data from Dexie.
    if (xhale.user_preference.offline_mode.is_enabled() && !skip_offline_access) {

      database.transaction("r", database.template_training_sessions, database.template_folders, function () {

        database.template_training_sessions.where('[athlete_id+template_sharing]').equals([athlete_id, TEMPLATE_SHARING_ONE_ATHLETE]).or('template_sharing').anyOf(TEMPLATE_SHARING_ALL_COACHES_ATHLETES, TEMPLATE_SHARING_ALL_COMPANIES_ATHLETES).toArray(function (template_sessions) {
          database.template_folders.where('[athlete_id+template_sharing]').equals([athlete_id, TEMPLATE_SHARING_ONE_ATHLETE]).or('template_sharing').anyOf(TEMPLATE_SHARING_ALL_COACHES_ATHLETES, TEMPLATE_SHARING_ALL_COMPANIES_ATHLETES).toArray(function (template_folders) {
            // Send the loaded data to the callback. There won't be anything to
            // delete until the server data loads and we can compare the
            // difference.
            //
            // TODO Training plans are not currently stored in the client
            // database, so they will not appear straight away.
            local_data = {template_sessions: template_sessions, template_folders: template_folders, training_plans: []};
            templates_loaded_callback(local_data, [], [], 'client');
          });
        }).then(function () {
          // Finished populating store with current database data. Now ajax in new data.
          fetchTemplatesFromServer(athlete_id, null, templatesFetchedFromServer);
        });

      }).catch(function (e) {
        // Something went wrong so fetch data from the server regardless.
        fetchTemplatesFromServer(athlete_id, null, templatesFetchedFromServer);
      });

    }
    else {
      // No attempt to load locally.
      fetchTemplatesFromServer(athlete_id, null, function (new_data) {templates_loaded_callback(new_data, [], [], 'server')});
    }

  }
  else if (group_id) {
    fetchTemplatesFromServer(null, group_id, function (new_data) {templates_loaded_callback(new_data, [], [], 'server')});
  }
  else {
    fetchTemplatesFromServer(null, null, function (new_data) {templates_loaded_callback(new_data, [], [], 'server')});
  }
}

/**
 * Save training sessions and template sessions to persistent storage.
 *
 * Any fields that are not provided are ignored and keep their original value.
 *
 * At the moment the data is only stored locally after it is successfully saved
 * to the server. This ensures the data passes validation. However, for offline
 * to work this is going to have to be reversed. In that case, I think there
 * would have to be some sort of temporary store of the new data alongside the
 * old data which is queued up to be saved on the server. And local validation.
 */
export var saveTrainingSessions = function (training_sessions, activity_plans, successCallback, errorCallback) {

  var storeSavedSessionLocally = function (data, status) {

    if ('errors' in data) {
      // Data didn't save on server, so don't save it locally either.
      return;
    }

    var sessions_to_store_locally = {};
    for (var index = 0; index < data.training_sessions.length; index++) {
      var training_session_data = data.training_sessions[index];
      if (training_session_data.template === true) {
        sessions_to_store_locally[training_session_data.id] = training_session_data;
      }
    }
    storeTemplatesLocally({template_sessions: sessions_to_store_locally, template_folders: {}});

  }

  // Allow for the passing of a single training session.
  if (training_sessions.constructor !== Array) {
    training_sessions = [training_sessions]
  }

  saveTrainingSessionsToServer(training_sessions, activity_plans, [successCallback, storeSavedSessionLocally], errorCallback );
}

export const saveTrainingSessionsToServer = function (training_sessions, activity_plans, successCallbacks, errorCallbacks) {
  let post_data = {};
  post_data.training_sessions = training_sessions;
  post_data.caller_id = 'saveTrainingSessionsToServer';

  if (activity_plans) {
    post_data.activity_plans = [];
    for (let i = 0; i < activity_plans.length; i++) {
      post_data.activity_plans.push(activity_plans[i].getSaveData());
    }
  }

  for (let i = 0; i < training_sessions.length; i++) {

    // Check for missing data when saving and log a debug message when it happens.
    if ((typeof(training_sessions[i].id) == 'undefined' || (isNaN(training_sessions[i].id) && training_sessions[i].id.substr(0,4) == 'temp')) &&
      typeof(training_sessions[i].template) == false &&
      typeof(training_sessions[i].uuid) == 'undefined' &&
      typeof(training_sessions[i].athlete_id) == 'undefined' &&
      typeof(training_sessions[i].group_id) == 'undefined' &&
      typeof(training_sessions[i].training_plan_id) == 'undefined')
    {
      logMessage('Missing data when saving training session: ' + JSON.stringify(training_sessions[i]));
    }

    // Ensure there is a save time stamp set.
    if (typeof(training_sessions[i].save_timestamp) == 'undefined') {
      training_sessions[i].save_timestamp = Date.now();
    }

  }

  ajaxRequest({
    url: '/coach/save-sessions/',
    method: 'POST',
    data: post_data,
    success: successCallbacks,
    expected_response_type: 'json',
    error: errorCallbacks,
  });
}

/**
 * Delete a training session.
 */
export var deleteTrainingSession = function (training_session_id, successCallback, errorCallback) {

  let deleteDataLocallyCallback = function (data, status, result) {
    deleteTemplateSessionsLocally([training_session_id], function () {successCallback(data, status, result)} );
  }

  let post_data = { 'training_session_id': training_session_id, 'csrfmiddlewaretoken': getCookie('csrftoken') };
  ajaxRequest({
    url: '/coach/delete-training-session/',
    method: 'POST',
    data: post_data,
    success: deleteDataLocallyCallback,
    error: errorCallback,
    expected_response_type: 'json',
  });
}


